/**
 * **THE DOMAIN PACKAGE'S OWN WORDS, IN ENGLISH** (Spanish for 3.0.11, Evan 2026-09-28). Every user-facing line the
 * helpers in `@kk/domain` build — the home greeting, "to approve", the dates, the schedule words, the consent lists,
 * the Quest checker's messages — is here, grouped by where it is used, and each helper reads its line from the table
 * for the language it is asked for (`domainCopy`). The helpers keep their signatures: the language is an optional last
 * argument, English by default, so every caller that passes none reads exactly what it read before.
 *
 * Leaves are strings, arrays of strings, or functions for a line with a value in it. A function takes the values only —
 * never a rule of this package — so a table can say a sentence in its own order ("de Priya", "hace 3 días").
 *
 * **What is NOT here, and why:**
 *  · The Quest checker's rules about the lesson TEXT (`PARENT_WORDS`, `PRONOUNS`, `STOP`, `MONEY`, `numberWords`,
 *    `wordsForNonMoney` in `rules/questCheck.ts`) — they read English lessons; Spanish lessons need Spanish rules, not a
 *    translation of these.
 *  · `FAMILY_VOICE_LINES` and the Family Voice script — a recorded, versioned script; a Spanish one is a new version.
 *  · Messages only a developer reads (the guide's patch problems, the crash rules, `validateGiveOops`, the rrule
 *    parser's throws, `client/errors`) and every value on the wire (bands, rule ids, kinds, codes).
 *  · `plural` / `pluralWord` / `formatBB` / `formatBambooBucks`: they take the caller's own nouns, or product names that
 *    stay English ("3 Bamboo Bucks").
 */
export const en = {
  templates: {
    greeting: (firstName: string) => `Hi, ${firstName}`,
    toApprove: (n: number) => `${n} to approve`,
    toApproveTitle: 'To approve',
    ageBand: { prereader: 'pre-reader', reader: 'reader' },
    photo: 'photo',
    tapToSeePhoto: 'tap to see photo',
    approve: 'Approve',
    possessive: (name: string) => `${name}’s`,
    /** `owner` is the possessive, built by `possessive` — the one place its rule lives. */
    photoFrom: (owner: string) => `Photo from ${owner} device`,
    readAloudTo: (name: string) => `Read aloud to ${name}:`,
    quotedNote: (note: string) => `“${note}”`,
    redeemTitle: (title: string) => `Redeem: ${title}`,
    rewardRequest: 'Reward request',
    balance: (balance: number) => `balance ${balance}`,
    scoreOk: (needsScore: number) => `score OK (needs ${needsScore})`,
    saveChild: (name: string) => `Save ${name}`,
    howDoesUse: (name: string) => `How does ${name} use the app?`,
    pickChoresFor: (name: string) => `Pick a few chores for ${name}`,
    addChores: (n: number) => `Add ${n} ${n === 1 ? 'chore' : 'chores'}`,
    noChoresYet: (name: string) => `${name} has no chores yet`,
    /** `owner` is the possessive, as `photoFrom`'s. */
    noChoresYetBody: (owner: string) => `Add a few and ${owner} score starts building the first time one gets finished.`,
    wantsTitle: (firstName: string, rewardTitle: string) => `${firstName} wants ${rewardTitle}`,
    chargedOnRedeem: 'charged on redeem',
    ofBalance: (balance: number) => `of ${balance}`,
    wantsBody: (price: number, balance: number) =>
      `${price} Bamboo Buck${price === 1 ? '' : 's'} · ${balance} saved`,
    cancelledTitle: (firstName: string, rewardTitle: string) => `${firstName} cancelled ${rewardTitle}`,
    cancelledBody: (bb: string) => `${bb} back in the wallet`,
    paidOutBy: (kidName: string) => `Paid out by ${kidName}`,
    paidOut: 'Paid out',
    /** `rate` arrives as "10 Bamboo Bucks" or "1 Bamboo Buck"; `one` says which, for the verb. */
    dollarLine: (dollars: string, rate: string, _one: boolean) => `= ${dollars} · ${rate} is $1`,
    badgeCountPill: (n: number) => `${n} ${n === 1 ? 'badge' : 'badges'}`,
    adventureEyebrow: (displayNumber: number) => `ADVENTURE ${displayNumber}`,
    missionsDoneOf: (done: number, total: number) => `${done} of ${total}`,
    missionNodeLabel: (order: number, title: string) => `Mission ${order} · ${title}`,
    startMissionCta: (order: number) => `Start mission ${order}`,
    quickCheckEyebrow: (step: number, total: number) => `QUICK CHECK · ${step} OF ${total}`,
    adventureCompleteEyebrow: (displayNumber: number) => `ADVENTURE ${displayNumber} COMPLETE`,
    newBadgeLine: (earned: number, total: number) => `New badge · ${earned} of ${total}`,
    missionsRightLine: (missions: number, correct: number, total: number) =>
      `${missions} ${missions === 1 ? 'mission' : 'missions'} · ${correct} of ${total} right`,
    streakKeptLine: (day: number) => `Streak kept · day ${day}`,
    adventureUnlockedLine: (displayNumber: number, title: string) =>
      `Adventure ${displayNumber} unlocked · ${title}`,
    badgesEarnedPill: (n: number) => `${n} earned`,
    badgesToGoPill: (n: number) => `${n} to go`,
    badgeSharedTitle: (child: string) => `${child} shared a badge`,
    badgeSharedBody: (badgeCaption: string) => `${badgeCaption}. Tap to see it.`,
    rewardNew: {
      familyTitle: (title: string) => `New reward: ${title}`,
      familyBody: (bbPrice: number) => `${bbPrice} Bamboo Buck${bbPrice === 1 ? '' : 's'}`,
      orgTitle: (groupName: string) => `New reward from ${groupName}`,
      orgBody: (unlockTask: string) => `Finish ${unlockTask} to unlock it`,
    },
    screenTime: {
      doneTitle: (firstName: string) =>
        `${firstName} finished today’s chores. Approve them to unlock screen time.`,
      askTitle: (firstName: string) => `${firstName} asked to unlock screen time.`,
      backTitle: 'Screen time is back!',
      backApproved: 'Your chores are approved.',
      backUnlocked: (kidName: string) => `${kidName} unlocked it for today.`,
      backPaused: (kidName: string) => `${kidName} paused the lock for a week.`,
      approvedEarly: 'Your chores are approved. Screen time won’t pause today.',
      turnedOff: (kidName: string) => `${kidName} turned off the screen-time lock.`,
      changed: (kidName: string) => `${kidName} changed when screen time pauses.`,
    },
    clubJoinedTitle: (club: string) => `${club} is on Kiddie Kredit. They can invite your village now.`,
    clubApplicationThanks: (name: string) => `Thanks, ${name}. We’ll be in touch within a few days.`,
  },

  time: {
    weekdaysLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    am: 'am',
    pm: 'pm',
    clock: (hour: number, minute: string, amPm: string) => `${hour}:${minute} ${amPm}`,
    shortDate: (day: number, month: string) => `${day} ${month}`,
    dateHeader: (weekday: string, day: number, month: string) => `${weekday}, ${day} ${month}`,
    weekRangeSameMonth: (startDay: number, endDay: number, month: string) => `${startDay}–${endDay} ${month}`,
    weekRange: (startDay: number, startMonth: string, endDay: number, endMonth: string) =>
      `${startDay} ${startMonth} – ${endDay} ${endMonth}`,
    dayMonthYear: (day: number, month: string, year: number) => `${day} ${month} ${year}`,
    monthYear: (month: string, year: number) => `${month} ${year}`,
    todayCapital: 'Today',
    yesterdayCapital: 'Yesterday',
    today: 'today',
    yesterday: 'yesterday',
    daysAgo: (n: number) => `${n} days ago`,
    /** The locale a day-group heading is upper-cased in. */
    upperLocale: 'en-US',
  },

  schedule: {
    daily: 'daily',
    everyDay: 'every day',
    schoolDays: 'school days',
    monFri: 'Mon–Fri',
    weekdaysPlural: ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'],
  },

  sizes: { S: 'Small', M: 'Medium', L: 'Large' },

  bands: { Building: 'Building', Growing: 'Growing', Good: 'Good', Great: 'Great', Super: 'Super' },

  oops: {
    /** The locale a reason's first letter is lower-cased in after "Oops:". */
    lowerLocale: 'en-US',
    reasonLabel: (reasonText: string) => `Oops: ${reasonText}`,
    noticeTitle: (giver: string, child: string, reasonLabel: string) => `${giver} gave ${child} an ${reasonLabel}.`,
    scorePart: (displayPoints: number) => `−${displayPoints} on the score`,
  },

  gate: {
    needsScore: (minScore: number) => `needs score ${minScore}`,
    noScoreYet: (minScore: number) =>
      `no score yet: a gate cannot be met before the first approved chore (needs ${minScore})`,
  },

  org: {
    sees: {
      firstName: 'Their first name',
      avatar: 'Their avatar',
      ageBand: 'Their age band — not their birthday',
      groups: 'Which of the club’s groups they are in',
      assignments: 'The club’s own assignments for them',
      tasks: 'How many of the club’s tasks they finished on time',
      enrolledAt: 'The day you joined',
      scoreBand: 'Their score BAND — "Good", never the number',
    },
    neverSees: {
      lastName: 'Their surname',
      dob: 'Their date of birth',
      photos: 'Any chore photo — those stay in your family',
      scoreNumber: 'Their score as a number',
      bb: 'Their Bamboo Bucks, or anything they spent them on',
      siblings: 'Their brothers and sisters',
      familyChores: 'Chores you set at home',
      wishes: 'What your child wishes for',
      oops: 'Any Oops, or why it was given',
      parentContact: 'Your email or anything about you',
    },
    neverSeesShort: {
      identity: 'Your child’s last name or birthday',
      photos: 'Chore photos, wishes and Oops — those stay in your family',
      home: 'Chores you set at home, or their Bamboo Bucks',
      score: 'Their Kredit Score, unless you switch it on',
    },
    initialsTooLong: (max: number, fits: string, tag: string) =>
      `initials go in a ${max}-character badge disc — "${fits}" fits, "${tag}" does not. The readable short form belongs in shortName`,
  },

  pandas: {
    'panda-white': 'the black and white panda with a sweater',
    'panda-orange': 'the orange panda with a cap',
    'panda-blue': 'the blue panda with glasses',
    'panda-red': 'the red panda with a flower',
    'panda-purple': 'the purple panda with headphones',
    'panda-green': 'the green panda with a pom-pom hat',
    'panda-yellow': 'the yellow panda with a scarf',
    'panda-brown': 'the brown panda with a bow tie',
  },

  village: {
    feeLine: 'We may earn a fee if you sign up.',
    partnerTag: 'Partner',
    helpfulThanks: 'Thanks for telling us.',
    problems: {
      pickShelf: 'pick a shelf',
      title: 'a title',
      titleMax: (max: number) => `a title of ${max} characters or fewer`,
      lineMax: (max: number) => `a line of ${max} characters or fewer`,
      ages: 'ages from youngest to oldest, 0 to 18',
      pictureKinds: 'a picture on a guide or printable only',
      picture: 'a picture from the icon library',
      printableMinutes: 'how long it takes, in minutes',
      readingTime: 'a reading time in minutes',
      guideText: 'the guide’s text',
      shorterText: 'shorter text',
      whichPrintable: 'which printable',
      eventStart: 'when the event starts',
      register: 'where to register',
      buttonGoes: 'where the button goes',
      whichPartner: 'which partner',
      partnerShelf: 'a partner’s card goes under Help with your money or Partner offers',
      https: 'a link starting https://',
      buttonWords: 'the button’s words',
      pictureDraft: 'a picture that is published (this icon is still a draft)',
      pictureRetired: 'a picture that is still offered (this icon is retired)',
    },
    logo: {
      type: 'a PNG or SVG file',
      file: 'the file',
      size: 'a file of 2 MB or less',
      png: 'a PNG file',
      svg: 'an SVG file',
    },
  },

  help: {
    question: 'a question',
    questionMax: (max: number) => `a question of ${max} characters or fewer`,
    answer: 'an answer',
    paragraphsMax: (max: number) => `an answer of ${max} paragraphs or fewer`,
    paragraphMax: (max: number) => `paragraphs of ${max} characters or fewer`,
    order: 'a whole-number order',
  },

  contentTeam: {
    soloPublishNote: 'published by its own author, the only Publisher',
  },

  adventure: {
    needsTitle: 'an adventure needs a title',
    learnOff: (title: string) => `Took "What you'll learn" off ${title}`,
    learnNeedsWords: `"What you'll learn" needs words; send null to take it off`,
    learnEdited: (title: string) => `Edited "What you'll learn" for ${title}`,
    noLearnToApprove: `there is no "What you'll learn" to approve`,
    learnApproved: (title: string) => `Approved "What you'll learn" for ${title}`,
    learnWithdrew: (title: string) => `Withdrew "What you'll learn" for ${title}`,
    edited: (title: string) => `Edited the adventure ${title}`,
    reorder: (missions: number) => `the new order must name each of the adventure's ${missions} missions exactly once`,
    nothingToPublish: 'nothing to publish: what is live is the latest version',
    approveLearnFirst: `approve "What you'll learn" before publishing: the approved line stays live until then`,
  },

  /** Where a field sits in a mission, as O13 and the checker print it ("Beat 2 · reader", "Question 1 · tile 3"). */
  questWhere: {
    beatReader: (beat: number) => `Beat ${beat} · reader`,
    beatPrereader: (beat: number) => `Beat ${beat} · pre-reader`,
    beatChoice: (beat: number, choice: string) => `Beat ${beat} · choice ${choice}`,
    beatChoiceConsequence: (beat: number, choice: string) => `Beat ${beat} · choice ${choice} consequence`,
    question: (question: number) => `Question ${question}`,
    questionPrereader: (question: number) => `Question ${question} · pre-reader`,
    questionTile: (question: number, tile: number) => `Question ${question} · tile ${tile}`,
    questionTilePicture: (question: number, tile: number) => `Question ${question} · tile ${tile} picture`,
    questionTiles: (question: number) => `Question ${question} · tiles`,
    questionFeedbackRight: (question: number) => `Question ${question} · feedback when right`,
    questionFeedback: (question: number) => `Question ${question} · feedback`,
    questionAnswer: (question: number) => `Question ${question} · answer`,
    missionPicture: 'Mission picture',
    badge: 'Badge',
    mission: 'Mission',
    readerScript: 'Reader script',
    prereaderScript: 'Pre-reader script',
  },

  /** O13's diff: each changed field's label, and how a tile's picture reads. */
  questDiff: {
    title: 'Title',
    levelName: 'Level name',
    mechanic: 'Mechanic',
    pointsAt: 'Points at',
    badge: 'Badge',
    picture: 'Picture',
    none: 'none',
    typeChosen: 'type, chosen',
    fromTileId: 'from the tile id',
  },

  questCheck: {
    rules: {
      'spoken-missing': 'No spoken form',
      'beat-count': 'Beat count',
      'too-long': 'Over the line limit',
      'answer-not-tile': 'Answer is not a tile',
      'tile-count': 'Tile count',
      'mixed-row': 'Mixed picture and type row',
      'unknown-icon': 'Unknown icon',
      'retired-icon': 'Retired icon',
      'draft-icon': 'Icon not published yet',
      'no-consequence': 'Choice with no consequence',
      'consequence-digits': 'Digits in a consequence',
      bb: '“BB” where a child reads',
      'parent-word': 'Mom or Dad in child text',
      pronoun: 'Pronoun about a child',
      unreviewed: 'Unreviewed script',
      duplicate: 'Repeats another mission',
      'same-icon': 'Same icon twice',
      'reading-level': 'Reading level',
      'long-mission': 'Longer than its siblings',
      'line-break': 'Line break in text',
    },
    notInLibrary: (key: string) => `“${key}” is not in the icon library.`,
    notPublished: (key: string) => `“${key}” is not published yet. Publish it in the icon library first.`,
    beatCount: (beats: number) => `A story mission has 2 or 3 beats. This one has ${beats}.`,
    nothingToHear: 'Nothing for a pre-reader to hear.',
    noConsequence: 'This choice has no consequence.',
    tileCount: (tiles: number) => `A question has 2 to 4 tiles. This one has ${tiles}.`,
    answerNotTile: 'The answer is not one of the tiles.',
    retired: (key: string) => `“${key}” is retired. It still draws where it is used, but pick another for new content.`,
    retiredName: (art: string, key: string) =>
      `“${art}” is a retired name. It still draws, as “${key}”, for content that already uses it.`,
    useKey: (key: string) => `Use ${key}`,
    /** `missing` is already quoted and joined ("“Save”, “Spend”"); `count` is how many draw as type. */
    mixedRow: (pictures: number, tiles: number, missing: string, count: number) =>
      `${pictures} of ${tiles} tiles have a picture; ${missing} ${count === 1 ? 'draws' : 'draw'} as type, so the row looks like it is missing icons.`,
    drawAllAsType: 'Draw every tile as type',
    sameIcon: (key: string) => `Two tiles draw “${key}”.`,
    bb: 'Write “Bamboo Bucks”, or leave the unit off.',
    parentWord: (word: string) => `“${word}”. The rule is role-neutral: “a grown-up”.`,
    pronoun: (word: string) => `“${word}” is about a child who is not Kai or Pip.`,
    digits: 'Write numbers as words. Money amounts may stay as digits.',
    digitsFix: 'Write the numbers as words',
    lineBreak: 'The app sets line breaks, so this one comes out.',
    lineBreakFix: 'Remove the line break',
    readingLevel: (words: number, spoken: boolean, limit: number) =>
      `A sentence of ${words} words. ${spoken ? 'Pre-readers follow' : 'Readers 9–12 follow'} ${limit} or fewer.`,
    tooLong: (lines: number, holds: number) => `${lines} lines at 360pt. It holds ${holds}.`,
    needsReview: 'Needs a review.',
    duplicate: (label: string, where: string, text: string) => `Close to ${label}, ${where}: “${text}”`,
    longMission: (words: number, median: number) =>
      `${words} words, against ${median} for a typical mission in this adventure.`,
  },
};

export type DomainCopy = typeof en;
